import React, { useState } from 'react';
import { XIcon } from 'lucide-react';
import { overrideReasons } from '../../data/overrideReasons';
export type ReviewIntent = 'override' | 'demote' | 'dnq';
interface OverrideFormProps {
  leadName: string;
  intent?: ReviewIntent;
  onCancel: () => void;
  onSubmit: (reasonCode: string, reasonSubcode: string) => void;
}
const copy: Record<ReviewIntent, {
  title: string;
  description: string;
  submit: string;
}> = {
  override: {
    title: 'Log override reason',
    description: 'Recorded against {name} and fed back into the grading rules.',
    submit: 'Save override'
  },
  demote: {
    title: 'Demote from hot list',
    description: '{name} moves to nurture; the reason is logged for rule tuning.',
    submit: 'Demote lead'
  },
  dnq: {
    title: 'Mark does not qualify',
    description: '{name} is removed from the queue and suppressed from re-flagging.',
    submit: 'Mark DNQ'
  }
};
export function OverrideForm({
  leadName,
  intent = 'override',
  onCancel,
  onSubmit
}: OverrideFormProps) {
  const [code, setCode] = useState(intent === 'dnq' ? 'DNQ' : '');
  const [subcode, setSubcode] = useState('');
  const selected = overrideReasons.find(reason => reason.code === code);
  const canSubmit = Boolean(code && subcode);
  const text = copy[intent];
  return <form className="border-t border-slate-200 bg-slate-50 px-4 py-3" onSubmit={event => {
    event.preventDefault();
    if (canSubmit) onSubmit(code, subcode);
  }} data-id="element-163" data-mp="334reoek43dru">
      <div className="mb-3 flex items-start justify-between gap-3" data-id="element-164" data-mp="3ux07n8gptnzo">
        <div data-id="element-165" data-mp="3gwxrprryx4n2">
          <h4 className="text-sm font-semibold text-slate-900" data-id="element-166" data-mp="3gxgyccc646ls">{text.title}</h4>
          <p className="text-xs text-slate-600" data-id="element-167" data-mp="3lw20wiew72de">
            {text.description.replace('{name}', leadName)}
          </p>
        </div>
        <button type="button" onClick={onCancel} className="rounded p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" aria-label="Cancel" data-id="element-168" data-mp="25hit6jk424pu">
          <XIcon className="h-4 w-4" data-id="element-169" data-mp="1md5qlu3sxytm" />
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2" data-id="element-170" data-mp="1mdyvsqn44g9n">
        <label className="block text-xs font-medium text-slate-700" data-id="element-171" data-mp="1hlxgswyk8bfx">
          Reason code
          <select value={code} onChange={event => {
          setCode(event.target.value);
          setSubcode('');
        }} className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-[#0176d3] focus:outline-none focus:ring-1 focus:ring-[#0176d3]" data-id="element-172" data-mp="1cnvvs6hkq21t">
            <option value="" data-id="element-173" data-mp="ypp6vfulrcea">Select…</option>
            {overrideReasons.map(reason => <option key={reason.code} value={reason.code} data-id="element-174" data-mp="tqkxop7oheny">
                {reason.code} — {reason.label}
              </option>)}
          </select>
        </label>

        <label className="block text-xs font-medium text-slate-700" data-id="element-175" data-mp="2o32ebdy0hgjm">
          Subcode
          <select value={subcode} onChange={event => setSubcode(event.target.value)} disabled={!selected} className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 focus:border-[#0176d3] focus:outline-none focus:ring-1 focus:ring-[#0176d3]" data-id="element-176" data-mp="2vl8x70d3xeta">
            <option value="" data-id="element-177" data-mp="2cp0ofmkvylt8">{selected ? 'Select…' : 'Pick a reason first'}</option>
            {selected?.subcodes.map(item => <option key={item} value={item} data-id="element-178" data-mp="2mldhqjciqxng">
                {item}
              </option>)}
          </select>
        </label>
      </div>

      <div className="mt-3 flex justify-end gap-2" data-id="element-179" data-mp="2qnqpfdatt5mv">
        <button type="button" onClick={onCancel} className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-180" data-mp="1ven1bkhil2s">
          Cancel
        </button>
        <button type="submit" disabled={!canSubmit} className="rounded bg-[#0176d3] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#014486] disabled:cursor-not-allowed disabled:bg-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-1" data-id="element-181" data-mp="3vmdp2e8lhujn">
          {text.submit}
        </button>
      </div>
    </form>;
}